/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import cx from 'classnames';
import { RelatedPartCheckboxName } from './RelatedPartCheckboxName';
import type { MappedAffinityProduct } from '../../../../interface';
import styles from './RelatedPartsCheckboxNameList.module.scss';

type Props = {
  parts: MappedAffinityProduct[];
  handleSelect: (skuId: string | null) => void;
};

const RelatedPartsCheckboxNameList = ({ parts, handleSelect }: Props) => {
  const distributeItems = () => {
    const totalItems = parts.length;

    // Case 1: 3 or fewer items - all in one column
    if (totalItems <= 3) {
      return {
        firstColumn: parts,
        secondColumn: [],
      };
    }

    // Case 2: 4-6 items - split into two columns (first 3 in first column, rest in second)
    if (totalItems <= 6) {
      return {
        firstColumn: parts.slice(0, 3),
        secondColumn: parts.slice(3),
      };
    }

    // Case 3: 7+ items - alternate between columns
    const firstColumn: MappedAffinityProduct[] = [];
    const secondColumn: MappedAffinityProduct[] = [];

    parts.forEach((part, index) => {
      if (index % 2 === 0) {
        firstColumn.push(part);
      } else {
        secondColumn.push(part);
      }
    });

    return { firstColumn, secondColumn };
  };

  const { firstColumn, secondColumn } = distributeItems();
  const showSecondColumn = secondColumn.length > 0;

  return (
    <div
      className={cx(styles.relatedPartsCheckboxNameList, styles.desktopListWrap)}
      data-testid="affinity-parts-checklist"
    >
      <div className={styles.column}>
        {firstColumn.map((part, index) => (
          <RelatedPartCheckboxName
            key={`${part.skuId ?? ''}_${index}`}
            part={part}
            handleSelect={handleSelect}
            isCurrentPDPPart={index === 0}
          />
        ))}
      </div>

      {showSecondColumn && (
        <div className={styles.column}>
          {secondColumn.map((part, index) => (
            <RelatedPartCheckboxName
              key={`${part.skuId ?? ''}_${secondColumn.length + index}`}
              part={part}
              handleSelect={handleSelect}
              isCurrentPDPPart={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export { RelatedPartsCheckboxNameList };
